import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import { divIcon } from "leaflet";
import { useEffect } from "react";
import { ConfigKey } from "enum/ConfigKey";
import { getConfig } from "config/app.config";
import {
  Box,
  Button,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import { IStation } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import greenMarker from "assets/green-marker.svg";
import { Star } from "lucide-react";
import { useStores } from "stores/setup/use-store";
import { useSaveCustomPoint } from "hooks/useSaveCustomPoint";

function FitToPoint({
  lat,
  lon,
  zoom = 10,
}: {
  lat: number;
  lon: number;
  zoom?: number;
}) {
  const map = useMap();

  useEffect(() => {
    map.setView([lat, lon], zoom);
  }, [lat, lon, zoom, map]);

  return null;
}

const pointMarkerIcon = divIcon({
  html: `<img src="${greenMarker}" alt="selected station" style="width: 25px; height: 30px;" />`,
  iconSize: [25, 30],
  iconAnchor: [12.5, 30],
  className: "",
});

interface MapPointPreviewProps {
  station: IStation;
  onEdit?: () => void;
  onClear?: () => void;
  t: (key: string) => string;
  hideEditButtons?: boolean;
  hideSaveStationButton?: boolean;
}

export function MapPointPreview({
  station,
  onEdit,
  onClear,
  t,
  hideEditButtons = false,
  hideSaveStationButton = false,
}: MapPointPreviewProps) {
  const mapTilerKey = getConfig(ConfigKey.REACT_APP_MAPTILER_KEY);
  const isCustomPoint = station.station_id === 0;

  const {
    dataStores: { authStore },
  } = useStores();

  const {
    isSaveModalOpen,
    onSaveModalClose,
    stationName,
    setStationName,
    isSaving,
    isSaved,
    handleSaveStation,
    handleOpenSaveModal,
  } = useSaveCustomPoint({ station, t });

  const saveStationIcon = isSaved ? (
    <Star size={16} fill="currentColor" style={{ marginBottom: "4px" }} />
  ) : (
    <Star size={16} style={{ marginBottom: "4px" }} />
  );

  return (
    <Box width="100%">
      {/* Station info header */}
      <Flex align="center" gap={2} mb={3} p={3} bg="gray.50" borderRadius="md">
        <Box>
          <Text fontWeight="medium" fontSize="sm" color="#242E38">
            {isCustomPoint ? t("customPoint") : station.station_name}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {station.station_lat.toFixed(4)}, {station.station_lon.toFixed(4)}
          </Text>
        </Box>
      </Flex>

      {/* Map preview */}
      <Box position="relative" width="100%" height="200px">
        <MapContainer
          center={[station.station_lat, station.station_lon]}
          zoom={10}
          style={{
            height: "200px",
            width: "100%",
            borderRadius: "8px",
            overflow: "hidden",
          }}
          scrollWheelZoom={false}
          dragging={false}
          doubleClickZoom={false}
          zoomControl={false}
          touchZoom={false}
          boxZoom={false}
          keyboard={false}
        >
          <TileLayer
            attribution={
              '\u003ca href="https://www.maptiler.com/copyright/" target="_blank"\u003e\u0026copy; MapTiler\u003c/a\u003e \u003ca href="https://www.openstreetmap.org/copyright" target="_blank"\u003e\u0026copy; OpenStreetMap contributors\u003c/a\u003e'
            }
            tileSize={512}
            zoomOffset={-1}
            minZoom={1}
            crossOrigin={true}
            url={`https://api.maptiler.com/maps/019b6b50-0ba6-7f22-825f-03de06fca8a8/{z}/{x}/{y}.webp?key=${mapTilerKey}`}
            zIndex={700}
          />

          <Marker
            position={[station.station_lat, station.station_lon]}
            icon={pointMarkerIcon}
          />

          <FitToPoint lat={station.station_lat} lon={station.station_lon} />
        </MapContainer>

        {!hideEditButtons && (
          <>
            <Button
              layerStyle="newDarkOcean"
              _hover={{ layerStyle: "newDarkOcean" }}
              position="absolute"
              top="10px"
              right="10px"
              zIndex={1000}
              size="sm"
              onClick={onEdit}
              w="150px"
              fontSize="16px"
            >
              {t("edit")}
            </Button>

            <Button
              layerStyle="newDarkOcean"
              _hover={{ layerStyle: "newDarkOcean" }}
              position="absolute"
              top="50px"
              right="10px"
              zIndex={1000}
              size="sm"
              onClick={onClear}
              w="150px"
              fontSize="16px"
            >
              {t("clear")}
            </Button>
          </>
        )}

        {/* Save station button - only for custom points */}
        {!hideSaveStationButton && isCustomPoint && !authStore.readOnlyUser && (
          <Button
            layerStyle="newDarkOcean"
            _hover={{ layerStyle: "newDarkOcean" }}
            position="absolute"
            bottom="20px"
            right="10px"
            zIndex={1000}
            size="sm"
            onClick={handleOpenSaveModal}
            w="150px"
            fontSize="14px"
            cursor={isSaved ? "not-allowed" : undefined}
            leftIcon={saveStationIcon}
            pointerEvents={isSaved ? "none" : "auto"}
          >
            {isSaved ? t("stationSaved") : t("saveStation")}
          </Button>
        )}
      </Box>

      {/* Modal for saving custom point as station */}
      <Modal isOpen={isSaveModalOpen} onClose={onSaveModalClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="white">
          <ModalHeader color="#1A202C">{t("saveStation")}</ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel color="#2D3748">{t("stationName")}</FormLabel>
              <Input
                value={stationName}
                onChange={(e) => setStationName(e.target.value)}
                placeholder={t("stationNamePlaceholder") as string}
                autoFocus
                color="#1A202C"
              />
            </FormControl>
            <Text fontSize="sm" color="#718096" mt={2}>
              {t("coordinates")}: {station.station_lat.toFixed(4)},{" "}
              {station.station_lon.toFixed(4)}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onSaveModalClose}
              color="#4A5568"
            >
              {t("cancel")}
            </Button>
            <Button
              layerStyle="ocean"
              _hover={{ layerStyle: "ocean" }}
              onClick={handleSaveStation}
              isLoading={isSaving}
              isDisabled={!stationName.trim()}
            >
              {t("save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
